import { Switch, Route, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";

import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/ui/theme-provider";
import { useAuth } from "@/hooks/useAuth";
import Landing from "@/pages/landing";
import Login from "@/pages/login";
import Signup from "@/pages/signup";
import ForgotPassword from "@/pages/forgot-password";
import ResetPassword from "@/pages/reset-password";
import VerifyEmail from "@/pages/verify-email";
import SignupInvite from "@/pages/signup-invite";
import Home from "@/pages/home";
import DocumentView from "@/pages/document-view";
import AccountSettings from "@/pages/account-settings";
import OrgSettings from "@/pages/org-settings";
import Onboarding from "@/pages/onboarding";
import NotFound from "@/pages/not-found";

// Public auth routes (always accessible regardless of auth state)
function PublicAuthRoutes() {
  return (
    <>
      <Route path="/signup/:token" component={SignupInvite} />
      <Route path="/signup" component={Signup} />
      <Route path="/login" component={Login} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/reset-password/:token" component={ResetPassword} />
      <Route path="/verify-email" component={VerifyEmail} />
    </>
  );
}

function Router() {
  const { isAuthenticated, isLoading, user } = useAuth();

  const needsOnboarding = isAuthenticated && user && (user as any).needsOnboarding === true;
  const needsEmailVerification = isAuthenticated && user && user.emailVerified === false;

  if (isLoading) {
    return null;
  }

  return (
    <Switch>
      {/* Always-accessible auth routes */}
      <Route path="/signup/:token" component={SignupInvite} />
      <Route path="/signup" component={Signup} />
      <Route path="/login" component={Login} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/reset-password/:token" component={ResetPassword} />
      <Route path="/verify-email" component={VerifyEmail} />

      {!isAuthenticated ? (
        <>
          <Route path="/" component={Landing} />
          <Route path="/doc/:documentId" component={DocumentView} />
          <Route path="/doc/:documentId/v/:versionId" component={DocumentView} />
          <Route path="/doc/:documentId/v/:versionId/p/:pageNumber" component={DocumentView} />
          <Route path="/doc/:documentId/comment/v/:versionId/p/:pageNumber" component={DocumentView} />
          <Route path="/doc/:documentId/comment/v/:versionId/p/:pageNumber/c/:indicatorNumber" component={DocumentView} />
          <Route path="/doc/:documentId/browse/v/:versionId" component={DocumentView} />
          <Route path="/doc/:documentId/compare/v/:versionIdA/vs/:versionIdB" component={DocumentView} />
          <Route path="/doc/:documentId/compare/:subMode/v/:versionIdA/vs/:versionIdB" component={DocumentView} />
          <Route path="/">{() => <Redirect to="/login" />}</Route>
        </>
      ) : needsEmailVerification ? (
        <>
          <Route path="/" component={VerifyEmail} />
          <Route>{() => <Redirect to="/verify-email" />}</Route>
        </>
      ) : needsOnboarding ? (
        <>
          <Route path="/onboarding" component={Onboarding} />
          <Route path="/doc/:documentId" component={DocumentView} />
          <Route path="/doc/:documentId/v/:versionId" component={DocumentView} />
          <Route path="/doc/:documentId/v/:versionId/p/:pageNumber" component={DocumentView} />
          <Route path="/doc/:documentId/comment/v/:versionId/p/:pageNumber" component={DocumentView} />
          <Route path="/doc/:documentId/comment/v/:versionId/p/:pageNumber/c/:indicatorNumber" component={DocumentView} />
          <Route path="/doc/:documentId/browse/v/:versionId" component={DocumentView} />
          <Route path="/doc/:documentId/compare/v/:versionIdA/vs/:versionIdB" component={DocumentView} />
          <Route path="/doc/:documentId/compare/:subMode/v/:versionIdA/vs/:versionIdB" component={DocumentView} />
          <Route>{() => <Redirect to="/onboarding" />}</Route>
        </>
      ) : (
        <>
          <Route path="/" component={Home} />
          <Route path="/onboarding" component={Onboarding} />
          <Route path="/account-settings" component={AccountSettings} />
          <Route path="/org-settings" component={OrgSettings} />
          <Route path="/doc/:documentId" component={DocumentView} />
          <Route path="/doc/:documentId/v/:versionId" component={DocumentView} />
          <Route path="/doc/:documentId/v/:versionId/p/:pageNumber" component={DocumentView} />
          <Route path="/doc/:documentId/comment/v/:versionId/p/:pageNumber" component={DocumentView} />
          <Route path="/doc/:documentId/comment/v/:versionId/p/:pageNumber/c/:indicatorNumber" component={DocumentView} />
          <Route path="/doc/:documentId/browse/v/:versionId" component={DocumentView} />
          <Route path="/doc/:documentId/compare/v/:versionIdA/vs/:versionIdB" component={DocumentView} />
          <Route path="/doc/:documentId/compare/:subMode/v/:versionIdA/vs/:versionIdB" component={DocumentView} />
        </>
      )}
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="dark">
        <TooltipProvider>
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
